import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { 
  FaCar, 
  FaSignOutAlt,
  FaBars, // Hamburger icon
  FaTimes // Close icon
} from 'react-icons/fa';

const Navbar = () => {
  // State for Mobile Menu visibility
  const [isOpen, setIsOpen] = useState(false);
  // State for logged-in user details
  const [user, setUser] = useState(null); 
  
  const linkStyle = "text-white text-sm font-semibold hover:text-amber-400 transition-colors duration-300";
  const activeLinkStyle = "text-amber-400 text-sm font-semibold";
  
  // --- CRITICAL FIX: Set live Render Host here ---
  const API_HOST = 'https://quick-wheels-2.onrender.com';
  const API_URL = `${API_HOST}/api/auth`; 

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_URL}/current_user`, {
        credentials: 'include' 
      });
      if (response.ok) {
        const userData = await response.json();
        // Check if userData has an ID (meaning user is logged in)
        setUser(userData?.id ? userData : null); 
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setUser(null); 
    }
  };

  const handleLogout = () => {
    // Redirects to the live Render backend logout endpoint
    window.location.href = `${API_URL}/logout`; 
  };
  
  // Toggle the mobile menu state
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  // Close menu after a link is clicked
  const handleNavClick = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';

  return (
    <nav className="bg-black text-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div className="py-4 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <FaCar className="h-8 w-8 text-amber-400" />
            <span className="ml-2 text-xl font-bold text-white animate-quick-wheels">Quick Wheels</span>
          </Link>

          {/* 1. DESKTOP Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <NavLink to="/" className={({ isActive }) => isActive ? activeLinkStyle : linkStyle}>Home</NavLink>
            <NavLink to="/vehicles" className={({ isActive }) => isActive ? activeLinkStyle : linkStyle}>Vehicles</NavLink>
            <NavLink to="/about" className={({ isActive }) => isActive ? activeLinkStyle : linkStyle}>About Us</NavLink>
            <NavLink to="/contact" className={({ isActive }) => isActive ? activeLinkStyle : linkStyle}>Contact Us</NavLink>
          </div>

          {/* User/Auth and Mobile Button Container */}
          <div className="flex items-center space-x-4">
            
            {/* 1. DESKTOP Auth/User Section */}
            <div className="hidden md:flex items-center space-x-4">
                {user ? (
                    <div className="flex items-center space-x-3">
                        <NavLink 
                            to="/dashboard"
                            className={({ isActive }) => isActive ? activeLinkStyle : linkStyle}
                        >
                            Dashboard
                        </NavLink>
                        
                        <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-950 font-bold flex items-center justify-center text-sm ring-2 ring-amber-400">
                            {userInitial}
                        </div>
                        
                        <button
                            onClick={handleLogout}
                            title="Sign Out"
                            className="text-white hover:text-red-500 transition-colors duration-300 p-2 rounded-full hover:bg-slate-700/50"
                        >
                            <FaSignOutAlt size={18} />
                        </button>
                    </div>
                ) : (
                    <><Link 
                        to="/login"
                        className="text-white text-sm font-semibold py-2 px-4 rounded-lg border border-amber-400 hover:bg-amber-400 hover:text-slate-950 transition-colors duration-300"
                    >
                        Login
                    </Link>
                    <Link
                        to="/signup"
                        className="text-white text-sm font-semibold py-2 px-4 rounded-lg border border-amber-400 hover:bg-amber-400 hover:text-slate-950 transition-colors duration-300"
                    >
                        Sign Up
                    </Link>
                    </>
                )}
            </div>
            
            {/* 2. MOBILE Hamburger Button */}
            <button 
                onClick={toggleMenu} 
                className="md:hidden text-white focus:outline-none"
            >
                {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* 3. MOBILE Menu Overlay */}
      <div 
        className={`md:hidden absolute w-full bg-black shadow-lg transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100 py-2' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="flex flex-col space-y-3 px-4 pb-4 pt-2">
          
          {/* Main Links */}
          <NavLink to="/" onClick={handleNavClick} className={({ isActive }) => `${isActive ? activeLinkStyle : 'text-white'} text-lg hover:text-amber-400`}>Home</NavLink>
          <NavLink to="/vehicles" onClick={handleNavClick} className={({ isActive }) => `${isActive ? activeLinkStyle : 'text-white'} text-lg hover:text-amber-400`}>Vehicles</NavLink>
          <NavLink to="/about" onClick={handleNavClick} className={({ isActive }) => `${isActive ? activeLinkStyle : 'text-white'} text-lg hover:text-amber-400`}>About Us</NavLink>
          <NavLink to="/contact" onClick={handleNavClick} className={({ isActive }) => `${isActive ? activeLinkStyle : 'text-white'} text-lg hover:text-amber-400`}>Contact Us</NavLink>

          <div className="w-full h-px bg-slate-700 my-2"></div>
          
          {/* Mobile Auth Links */}
          {user ? (
            <div className="flex flex-col space-y-3">
                <NavLink 
                    to="/dashboard"
                    onClick={handleNavClick}
                    className="text-amber-400 font-bold text-lg"
                >
                    Dashboard ({userInitial})
                </NavLink>
                <button
                    onClick={() => { handleLogout(); handleNavClick(); }}
                    className="text-left text-red-500 font-semibold text-lg hover:text-red-400 transition-colors"
                >
                    <FaSignOutAlt className="inline mr-2" /> Logout
                </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-3">
              <Link 
                  to="/login"
                  onClick={handleNavClick}
                  className="text-amber-400 font-semibold py-2 px-4 rounded-lg border border-amber-400 text-center transition-colors duration-300"
              >
                  Login
              </Link>
              <Link
                  to="/signup"
                  onClick={handleNavClick}
                  className="text-amber-400 font-semibold py-2 px-4 rounded-lg border border-amber-400 text-center transition-colors duration-300"
              >
                  Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;